import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Application = Tables<'trade_applications'>

type TabFilter = 'pending' | 'approved' | 'rejected' | 'all'

const tabs: { key: TabFilter; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
]

export function ApplicationsPage() {
  const { user } = useAuth()
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<TabFilter>('pending')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const load = async () => {
    let query = supabase.from('trade_applications').select('*').order('created_at', { ascending: false })
    if (tab !== 'all') {
      query = query.eq('status', tab as Tables<'trade_applications'>['status'])
    }
    const { data } = await query
    setApplications(data || [])
    setLoading(false)
  }

  useEffect(() => { setLoading(true); load() }, [tab])

  const approve = async (app: Application) => {
    if (!user) return
    setActionLoading(true)

    // 1. Create organization from company data
    const { data: org } = await supabase.from('organizations').insert({
      name: app.company_name,
      slug: app.company_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      email: app.company_email || app.contact_email,
      is_active: true,
    }).select().single()

    if (org) {
      // 2. Update user profile: activate and assign org
      await supabase.from('profiles').update({
        is_active: true,
        organization_id: org.id,
        role: 'partner_admin' as Tables<'profiles'>['role'],
      }).eq('id', app.user_id)

      // 3. Update application status
      await supabase.from('trade_applications').update({
        status: 'approved' as Tables<'trade_applications'>['status'],
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        organization_id: org.id,
      }).eq('id', app.id)
    }

    setActionLoading(false)
    setExpandedId(null)
    load()
  }

  const reject = async (app: Application) => {
    if (!user || !rejectionReason.trim()) return
    setActionLoading(true)

    await supabase.from('trade_applications').update({
      status: 'rejected' as Tables<'trade_applications'>['status'],
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      rejection_reason: rejectionReason.trim(),
    }).eq('id', app.id)

    setActionLoading(false)
    setRejectingId(null)
    setRejectionReason('')
    load()
  }

  const pendingCount = applications.filter(a => a.status === 'pending').length

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>

  return (
    <div>
      <PageHeader
        title="Trade Applications"
        description={`Review and manage trade account applications${tab === 'all' && pendingCount > 0 ? ` — ${pendingCount} pending` : ''}`}
      />

      <div className={css.tabs}>
        {tabs.map(t => (
          <button
            key={t.key}
            className={`${css.tab} ${tab === t.key ? css.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'pending' && pendingCount > 0 && (
              <span style={{
                marginLeft: 6, background: 'var(--color-accent)', color: 'white',
                borderRadius: 10, padding: '1px 7px', fontSize: 'var(--text-xs)', fontWeight: 600,
              }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {applications.length === 0 ? (
        <div className={css.emptyState}>
          <h3>No {tab === 'all' ? '' : tab} applications</h3>
          <p>There are no {tab === 'all' ? '' : tab + ' '}trade account applications to display.</p>
        </div>
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Submitted</th>
                <th>Status</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <>
                  <tr
                    key={app.id}
                    className={css.tableClickableRow}
                    onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ fontWeight: 500 }}>{app.company_name}</td>
                    <td>{app.contact_first_name} {app.contact_last_name}</td>
                    <td style={{ fontSize: 'var(--text-sm)' }}>{app.contact_email}</td>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <span className={`${css.badge} ${
                        app.status === 'pending' ? css.badge_warning :
                        app.status === 'approved' ? css.badge_success :
                        css.badge_error
                      }`}>
                        {app.status}
                      </span>
                    </td>
                    <td>
                      {app.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }} onClick={e => e.stopPropagation()}>
                          <button
                            className={`${css.btnPrimary} ${css.btnSm}`}
                            onClick={() => approve(app)}
                            disabled={actionLoading}
                          >
                            Approve
                          </button>
                          <button
                            className={`${css.btnDanger} ${css.btnSm}`}
                            onClick={() => { setRejectingId(app.id); setRejectionReason('') }}
                            disabled={actionLoading}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {expandedId === app.id && (
                    <tr key={`${app.id}-detail`}>
                      <td colSpan={6} style={{ background: 'var(--color-bg-subtle)', padding: 'var(--space-5)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
                          <Detail label="Registered Name" value={app.registered_name} />
                          <Detail label="Ownership Type" value={app.ownership_type} />
                          <Detail label="Company Reg #" value={app.company_registration_number} />
                          <Detail label="VAT Number" value={app.vat_number} />
                          <Detail label="Address" value={[app.address_line1, app.address_line2].filter(Boolean).join(', ')} />
                          <Detail label="Postal Code" value={app.postal_code} />
                          <Detail label="Business Phone" value={app.business_phone} />
                          <Detail label="Company Email" value={app.company_email} />
                          <Detail label="Contact Phone" value={app.contact_phone} />
                          <Detail label="Requested Credit Limit" value={app.requested_credit_limit != null ? `£${Number(app.requested_credit_limit).toLocaleString()}` : null} />
                          {app.reviewed_at && <Detail label="Reviewed At" value={new Date(app.reviewed_at).toLocaleString()} />}
                          {app.rejection_reason && <Detail label="Rejection Reason" value={app.rejection_reason} />}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Rejection reason modal inline */}
                  {rejectingId === app.id && (
                    <tr key={`${app.id}-reject`}>
                      <td colSpan={6} style={{ background: 'var(--color-error-muted)', padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
                          <div className={css.formGroup} style={{ flex: 1 }}>
                            <label className={css.formLabel}>Rejection Reason</label>
                            <textarea
                              className={css.formTextarea}
                              value={rejectionReason}
                              onChange={e => setRejectionReason(e.target.value)}
                              placeholder="Provide a reason for rejection..."
                              style={{ minHeight: 60 }}
                            />
                          </div>
                          <button
                            className={`${css.btnDanger} ${css.btnSm}`}
                            onClick={() => reject(app)}
                            disabled={actionLoading || !rejectionReason.trim()}
                          >
                            Confirm Reject
                          </button>
                          <button
                            className={`${css.btnSecondary} ${css.btnSm}`}
                            onClick={() => { setRejectingId(null); setRejectionReason('') }}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value || '—'}</div>
    </div>
  )
}
